import appConfig from '../../config/appConfig';
import * as usageDateTypes from '../../consts/uam/usageDateTypes';
import {setUsageStats} from '../../state/ducks/usageStatistic';
import {dispatch} from '../../state/store';

const getDate = (date, isStart, minusDays = 0) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return new Date(year, month, day - minusDays, isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59);
};

const saveUsageStats = (usageStats, range) => {
    const data = usageStats.map((usageData) => mapUsageData(usageData, range));

    dispatch(setUsageStats(data));
};

const mapUsageData = (usageData, range) => {
    const dayRecords = mapDayRecords(usageData.records, range);
    const leviaDayRecords = mapDayRecords(usageData.records, range, true);
    const {record_count, assetId, isAriane, isP1, isP4, isP1V4} = usageData;

    return {
        count: record_count,
        dayRecords,
        leviaDayRecords,
        weekRecords: getWeekRecords(dayRecords, range),
        leviaWeekRecords: getWeekRecords(leviaDayRecords, range),
        assetId,
        isAriane,
        isP1,
        isP4,
        isP1V4,
    };
};

const mapDayRecords = (records, range, isLevia) => {
    const result = [];

    for (let d = getDate(range.from, true); d < range.to; d.setDate(d.getDate() + 1)) {
        const dateString = d.toISOString();
        const nextDay = new Date(dateString);

        nextDay.setDate(d.getDate() + 1);

        const recordsForDay = records.filter((record) => {
            const fromDate = new Date(record.from);

            return fromDate < nextDay && fromDate >= d;
        });

        const dayRecords = recordsForDay.map((record) => mapDayRecord(record, isLevia)).filter((x) => !!x);

        result.push({date: duplicateDate(d), records: addDayEmptyData(dayRecords)});
    }

    return result;
};

const mapDayRecord = (record, isLevia) => {
    const LEVIA_PROFILE_ID = 'profile-0x42503200';
    const leviaRecord = record[LEVIA_PROFILE_ID];

    if (isLevia && !leviaRecord) {
        return;
    }

    const {withinDeltaCount, outsideOfDeltaCount, from} = record;
    const {withinDeltaCount: leviaWithinDeltaCount = 0, outsideOfDeltaCount: leviaOutsideOfDeltaCount = 0} =
        leviaRecord || {};
    const count = isLevia ? leviaWithinDeltaCount : withinDeltaCount - leviaWithinDeltaCount;
    const outsideCount = isLevia ? leviaOutsideOfDeltaCount : outsideOfDeltaCount - leviaOutsideOfDeltaCount;

    const curTime = new Date(from).getHours();
    const getKey = (time, retryCount = 0) => {
        switch (time) {
            case 0:
                return usageDateTypes.RANGE_0_6;
            case 6:
                return usageDateTypes.RANGE_6_12;
            case 12:
                return usageDateTypes.RANGE_12_18;
            case 18:
                return usageDateTypes.RANGE_18_24;
            default:
                //if the hours is not expected then adjust it - because of time zone change
                //max diff in hours is 6 - so 3 in one front and 3 in other
                const diff = retryCount === 3 ? 4 : retryCount < 3 ? -1 : 1;
                const newTime = time + diff >= 24 ? 0 : time + diff;

                return getKey(newTime, retryCount + 1);
        }
    };

    return {key: getKey(curTime), count, isOutside: outsideCount > 0};
};

const getWeekRecords = (dayRecords, range) => {
    const isFirstDaySunday = appConfig.getIsFirstDaySunday();
    const startDate = getDate(range.from, true);
    const startDay = startDate.getDay();
    let diff = startDate.getDate() - startDay; //sunday

    if (!isFirstDaySunday) diff = diff + (startDay === 0 ? -6 : 1); //monday

    const startWeekDate = new Date(startDate.setDate(diff));
    const result = [];

    for (let d = startWeekDate; d < range.to; d.setDate(d.getDate() + 7)) {
        const dateString = d.toISOString();
        const nextWeekDay = new Date(dateString);

        nextWeekDay.setDate(d.getDate() + 7);

        const recordsForWeek = dayRecords.filter((record) => {
            const fromDate = new Date(record.date);

            return fromDate < nextWeekDay && fromDate >= d;
        });

        const weekRecords = recordsForWeek.map((recordForDay) => mapWeekUsageRecord(recordForDay));

        result.push({
            date: duplicateDate(d),
            records: addWeekEmptyData(d, weekRecords),
        });
    }

    return result;
};

const mapWeekUsageRecord = (recordForDay) => {
    let totalCount = 0;
    const isOutside = recordForDay.records?.some((record) => record.isOutside);

    recordForDay.records?.forEach((record) => (totalCount += record.count));

    return {key: recordForDay.date, count: totalCount, isOutside};
};

const addDayEmptyData = (dayRecords) => {
    const fullHourKeys = [
        usageDateTypes.RANGE_0_6,
        usageDateTypes.RANGE_6_12,
        usageDateTypes.RANGE_12_18,
        usageDateTypes.RANGE_18_24,
    ];

    fullHourKeys.forEach((hourKey) => {
        if (!dayRecords.map((hour) => hour.key).includes(hourKey)) {
            dayRecords.push({key: hourKey, count: 0, isOutside: false});
        }
    });

    dayRecords.sort((a, b) => a.key - b.key);
    return dayRecords;
};

const addWeekEmptyData = (date, weekRecords) => {
    const toDate = new Date(date);

    toDate.setDate(date.getDate() + 7);

    for (let d = new Date(date); d < toDate; d.setDate(d.getDate() + 1)) {
        if (!weekRecords.map((day) => day.key.toISOString()).includes(d.toISOString())) {
            weekRecords.push({key: duplicateDate(d), count: 0, isOutside: false});
        }
    }

    weekRecords.sort((a, b) => a.key - b.key);
    return weekRecords;
};

//removes variable link
const duplicateDate = (date) => new Date(date);

const sumUsageData = (data) => {
    return [
        getSumUsageData({data, isAriane: true}),
        getSumUsageData({data, isP1: true}),
        getSumUsageData({data, isP4: true}),
        getSumUsageData({data, isP1V4: true}),
    ];
};

const getSumUsageData = ({data, isAriane = false, isP1 = false, isP4 = false, isP1V4 = false}) => {
    const groupData = data.filter(
        (record) =>
            record.isAriane === isAriane && record.isP1 === isP1 && record.isP4 === isP4 && record.isP1V4 === isP1V4
    );

    const count = groupData.reduce((partialSum, a) => partialSum + a.count, 0);
    const dayData = groupData.map((x) => x.dayRecords);
    const dayRecords = getSumUsageRecords(dayData);
    const leviaDayData = groupData.map((x) => x.leviaDayRecords);
    const leviaDayRecords = getSumUsageRecords(leviaDayData);

    const weekData = groupData.map((x) => x.weekRecords);
    const weekRecords = getSumUsageRecords(weekData);
    const leviaWeekData = groupData.map((x) => x.leviaWeekRecords);
    const leviaWeekRecords = getSumUsageRecords(leviaWeekData);

    return {
        isAriane,
        isP1,
        isP4,
        isP1V4,
        dayRecords,
        leviaDayRecords,
        weekRecords,
        leviaWeekRecords,
        count,
    };
};

const getSumUsageRecords = (data) => {
    if (!data?.length) return [];

    const result = [];
    const firstItem = data[0];

    for (let i = 0; i < firstItem.length; i++) {
        const records = [];
        const keyItem = firstItem[i];

        for (let j = 0; j < keyItem.records.length; j++) {
            let count = 0;
            let isOutside = false;

            for (let t = 0; t < data.length; t++) {
                count += data[t][i].records[j].count;
                isOutside = isOutside || data[t][i].records[j].isOutside;
            }
            records.push({key: keyItem.records[j].key, count, isOutside});
        }

        const count = records.reduce((partialSum, a) => partialSum + a.count, 0);

        result.push({date: keyItem.date, records, count});
    }

    return result;
};

export default {
    getDate,
    saveUsageStats,
    sumUsageData,
};
